import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query'

import {
  Announcement,
  AnnouncementInput,
  AnnouncementOrganizationStatusValue,
  AnnouncementOrganizationsResponse,
  AnnouncementWithSummary,
  archiveAnnouncement,
  createAnnouncement,
  getAnnouncement,
  listAnnouncementOrganizations,
  listAnnouncements,
  publishAnnouncement,
  updateAnnouncement
} from '@/api/announcements-client'

export enum ANNOUNCEMENT_QUERY_IDS {
  PREFIX = 'ANNOUNCEMENTS',
  LIST = 'ANNOUNCEMENTS_LIST',
  GET = 'ANNOUNCEMENTS_GET',
  ORGANIZATIONS = 'ANNOUNCEMENTS_ORGANIZATIONS'
}

const ALL_ANNOUNCEMENT_QUERY_IDS = [
  ANNOUNCEMENT_QUERY_IDS.LIST,
  ANNOUNCEMENT_QUERY_IDS.GET,
  ANNOUNCEMENT_QUERY_IDS.ORGANIZATIONS
]

const useInvalidateAnnouncements = () => {
  const queryClient = useQueryClient()

  return () =>
    Promise.all(
      ALL_ANNOUNCEMENT_QUERY_IDS.map((id) =>
        queryClient.invalidateQueries({ queryKey: [id] })
      )
    )
}

export const useQueryGetAnnouncements = (
  options?: Partial<UseQueryOptions<AnnouncementWithSummary[]>>
) =>
  useQuery<AnnouncementWithSummary[]>({
    queryKey: [ANNOUNCEMENT_QUERY_IDS.LIST],
    queryFn: listAnnouncements,
    refetchOnWindowFocus: false,
    ...options
  })

export const useQueryGetAnnouncement = (
  announcementId: string,
  options?: Partial<UseQueryOptions<AnnouncementWithSummary>>
) =>
  useQuery<AnnouncementWithSummary>({
    queryKey: [ANNOUNCEMENT_QUERY_IDS.GET, announcementId],
    queryFn: () => getAnnouncement(announcementId),
    enabled: Boolean(announcementId),
    refetchOnWindowFocus: false,
    ...options
  })

export const useQueryGetAnnouncementOrganizations = (
  announcementId: string,
  status?: AnnouncementOrganizationStatusValue,
  options?: Partial<UseQueryOptions<AnnouncementOrganizationsResponse>>
) =>
  useQuery<AnnouncementOrganizationsResponse>({
    queryKey: [ANNOUNCEMENT_QUERY_IDS.ORGANIZATIONS, announcementId, status],
    queryFn: () => listAnnouncementOrganizations(announcementId, status),
    enabled: Boolean(announcementId),
    refetchOnWindowFocus: false,
    ...options
  })

export const useMutationCreateAnnouncement = () => {
  const invalidate = useInvalidateAnnouncements()

  return useMutation<Announcement, Error, AnnouncementInput>({
    mutationFn: createAnnouncement,
    onSuccess: () => invalidate()
  })
}

export const useMutationUpdateAnnouncement = () => {
  const invalidate = useInvalidateAnnouncements()

  return useMutation<
    Announcement,
    Error,
    { announcementId: string; input: Partial<AnnouncementInput> }
  >({
    mutationFn: ({ announcementId, input }) =>
      updateAnnouncement(announcementId, input),
    onSuccess: () => invalidate()
  })
}

export const useMutationPublishAnnouncement = () => {
  const invalidate = useInvalidateAnnouncements()

  return useMutation<Announcement, Error, string>({
    mutationFn: publishAnnouncement,
    onSuccess: () => invalidate()
  })
}

export const useMutationArchiveAnnouncement = () => {
  const invalidate = useInvalidateAnnouncements()

  return useMutation<Announcement, Error, string>({
    mutationFn: archiveAnnouncement,
    onSuccess: () => invalidate()
  })
}
